import { FormEvent, useMemo } from 'react';
import { ArrowRightLeft, Info, Loader2 } from 'lucide-react';

import InputField from '../../../components/Forms/Input/InputField';
import ToggleSwitch from '../../../components/Forms/Input/ToggleSwitch';
import FormModal from '../../../components/Modals/FormModal';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';

import { RestaurantUnitConversionForm } from '../../../types/restaurant/RestaurantUnitConversionTypes';
import { IProductUnit } from '../../../types/productUnit/productUnitInterfaces';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';

interface Props {
  isOpen: boolean;
  form: RestaurantUnitConversionForm;
  saving: boolean;
  units: IProductUnit[];
  onClose: () => void;
  onChange: <K extends keyof RestaurantUnitConversionForm>(
    field: K,
    value: RestaurantUnitConversionForm[K]
  ) => void;
  onSave: () => void;
}

const UnitConversionFormModal = ({
  isOpen,
  form,
  saving,
  units,
  onClose,
  onChange,
  onSave,
}: Props) => {
  const errors = useMemo(() => {
    const next: Record<string, string> = {};

    if (!form.fromUnitId) {
      next.fromUnitId = 'Seleccione la unidad origen.';
    }

    if (!form.toUnitId) {
      next.toUnitId = 'Seleccione la unidad destino.';
    }

    if (
      form.fromUnitId &&
      form.toUnitId &&
      Number(form.fromUnitId) === Number(form.toUnitId)
    ) {
      next.toUnitId = 'La unidad destino debe ser diferente.';
    }

    if (!form.factor || Number(form.factor) <= 0) {
      next.factor = 'El factor debe ser mayor que cero.';
    }

    return next;
  }, [form]);

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (!Object.keys(errors).length) {
      onSave();
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title={form.id ? 'Editar conversión' : 'Nueva conversión'}
      maxWidth="max-w-2xl"
      headerExtra={<RestaurantFormHelp topic="unitConversion" />}
    >
      <form onSubmit={submit} className="space-y-5">
        <div className="rounded-xl border border-stroke bg-gray-2/60 p-4 dark:border-strokedark dark:bg-boxdark-2">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <ArrowRightLeft className="h-5 w-5" />
            </span>

            <div>
              <h3 className="font-semibold text-black dark:text-white">
                Datos de la conversión
              </h3>

              <p className="mt-1 text-sm text-body dark:text-bodydark">
                Defina la equivalencia entre una unidad origen y una unidad
                destino.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
          <SimpleSearchSelect
            label="Unidad origen"
            name="fromUnitId"
            options={units.map((unit) => ({
              id: unit.id,
              name: unit.name,
            }))}
            selectedValue={form.fromUnitId ? Number(form.fromUnitId) : null}
            iconKey="box"
            required
            error={errors.fromUnitId}
            searchPlaceholder="Buscar unidad origen..."
            disabled={saving}
            onSelect={(selected) => onChange('fromUnitId', selected.id)}
          />

          <SimpleSearchSelect
            label="Unidad destino"
            name="toUnitId"
            options={units.map((unit) => ({
              id: unit.id,
              name: unit.name,
            }))}
            selectedValue={form.toUnitId ? Number(form.toUnitId) : null}
            iconKey="box"
            required
            error={errors.toUnitId}
            searchPlaceholder="Buscar unidad destino..."
            disabled={saving}
            onSelect={(selected) => onChange('toUnitId', selected.id)}
          />

          <InputField
            label="Factor de conversión"
            name="factor"
            type="number"
            iconKey="calculator"
            value={form.factor}
            step="any"
            min={0}
            required
            error={errors.factor}
            placeholder="Ej. 1000"
            onChange={(event) =>
              onChange(
                'factor',
                event.target.value === '' ? 0 : Number(event.target.value)
              )
            }
          />

          {form.id && (
            <div className="mb-4 rounded-lg border border-stroke bg-white px-4 py-3 shadow-1 dark:border-strokedark dark:bg-boxdark">
              <ToggleSwitch
                label="Registro activo"
                name="unitConversionActive"
                checked={form.active ?? true}
                onChange={() => onChange('active', !(form.active ?? true))}
              />

              <p className="mt-2 text-xs text-body dark:text-bodydark">
                Reactive esta conversión para volver a usarla en inventario y
                recetas.
              </p>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 text-primary">
              <Info className="h-5 w-5" />
            </span>

            <div className="text-sm text-body dark:text-bodydark">
              <p>
                El factor indica cuántas unidades destino equivalen a 1 unidad
                origen.
              </p>

              <p className="mt-1 font-medium text-black dark:text-white">
                Ejemplo: 1 Kilogramo = 1000 Gramos.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-stroke pt-5 dark:border-strokedark sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="
              rounded-lg border border-stroke px-5 py-2.5 text-sm font-medium
              text-black transition hover:border-primary hover:text-primary
              disabled:cursor-not-allowed disabled:opacity-50
              dark:border-strokedark dark:text-white
            "
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={saving || !!Object.keys(errors).length}
            className="
              inline-flex items-center justify-center gap-2 rounded-lg
              bg-primary px-5 py-2.5 text-sm font-medium text-white
              transition hover:bg-opacity-90 disabled:cursor-not-allowed
              disabled:opacity-50
            "
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}

            {saving
              ? 'Guardando...'
              : form.id
              ? 'Guardar cambios'
              : 'Crear conversión'}
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default UnitConversionFormModal;
